/**
 * Tri-state card used by the Estate Documents wizard screen.
 * Each document has three mutually-exclusive answers:
 *
 *   • "I already have one"   → will upload / share location later
 *   • "Help me create one"   → LegalZoom affiliate flow
 *   • "Not interested"       → decline (auto-hide from task list)
 *
 * The "Help me create one" option is hidden when
 * LEGALZOOM_AFFILIATE_ACTIVE is false so users don't hit a placeholder
 * link. Until the affiliate program is live the card shows only the
 * "have" / "decline" options.
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ms } from '../../../utils/responsive';
import { LEGALZOOM_AFFILIATE_ACTIVE } from '../../../constants/affiliateLinks';

export type DocumentCardAnswer = 'has' | 'create' | 'decline' | null;

type Props = {
  title: string;
  description: string;
  answer: DocumentCardAnswer;
  onChange: (a: DocumentCardAnswer) => void;
};

export const WizardDocumentCard: React.FC<Props> = ({
  title,
  description,
  answer,
  onChange,
}) => {
  const Pill = ({
    value,
    label,
    subLabel,
  }: {
    value: Exclude<DocumentCardAnswer, null>;
    label: string;
    subLabel?: string;
  }) => {
    const selected = answer === value;
    return (
      <TouchableOpacity
        style={[styles.pill, selected && styles.pillSelected]}
        onPress={() => onChange(value)}
        accessibilityRole="radio"
        accessibilityState={{ selected }}
        accessibilityLabel={label}
      >
        <Text style={[styles.pillLabel, selected && styles.pillLabelSelected]}>{label}</Text>
        {subLabel ? (
          <Text style={[styles.pillSubLabel, selected && styles.pillSubLabelSelected]}>
            {subLabel}
          </Text>
        ) : null}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.card}>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.description}>{description}</Text>
      <View style={styles.pillRow}>
        <Pill value="has" label="I have one" />
        {LEGALZOOM_AFFILIATE_ACTIVE ? (
          <Pill value="create" label="Help me create one" subLabel="via LegalZoom" />
        ) : null}
        <Pill value="decline" label="Not right now" />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(16),
    marginBottom: ms(14),
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
  },
  title: {
    fontSize: ms(17, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  description: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    marginBottom: ms(12),
    lineHeight: ms(18, 0.3),
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: ms(8),
  },
  pill: {
    paddingVertical: ms(10),
    paddingHorizontal: ms(14),
    borderRadius: ms(20),
    borderWidth: ms(1.5),
    borderColor: '#E1E5EA',
    backgroundColor: '#F9FAFB',
    alignItems: 'center',
  },
  pillSelected: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  pillLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  pillLabelSelected: {
    color: '#FFFFFF',
  },
  pillSubLabel: {
    fontSize: ms(11, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  pillSubLabelSelected: {
    color: '#E5F7F7',
  },
});
